import Component from '@glimmer/component';
import { service } from '@ember/service';
import { concat } from '@ember/helper';
import { t } from 'ember-intl';
import { Section, VStack } from 'tio-ui/components/layout';
import Text from 'tio-ui/components/text';
import Switch from 'tio-ui/components/switch';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import type { BetaProduct } from 'tio-employee/services/beta-experience';
import type PartnerService from 'tio-employee/services/partner';

/**
 * Beta Features section for the Profile page.
 *
 * Shows a global toggle plus per-product toggles to opt in to beta-quality
 * features, and the visual experiments (chamfered edges) that ride the same
 * flag but not the master switch. Only shows toggles for products the
 * employee actually has access to. Only visible when the `betaExperience`
 * feature flag is enabled (non-production envs).
 */
export default class BetaFeaturesSection extends Component {
  @service declare betaExperience: BetaExperienceService;
  @service declare partner: PartnerService;

  get availableProducts(): BetaProduct[] {
    const products: BetaProduct[] = [];
    if (this.partner.hasNewTuitionAssistance) {
      products.push('ta');
    }
    return products;
  }

  handleToggle = (checked: boolean) => {
    this.betaExperience.setEnabled(checked);
  };

  handleProductToggle = (product: BetaProduct, checked: boolean) => {
    this.betaExperience.setProductEnabled(product, checked);
  };

  handleChamferToggle = (checked: boolean) => {
    this.betaExperience.setChamferEnabled(checked);
  };

  <template>
    {{#if this.betaExperience.flagEnabled}}
      <Section>
        <:header>{{t "profile.beta.title"}}</:header>
        <:body>
          <VStack class="gap-4">
            <Text @variant="muted" @size="sm">
              {{t "profile.beta.description"}}
            </Text>
            <Switch
              data-test-id="beta-features-switch"
              @label={{t "profile.beta.toggle_label"}}
              @hint={{t "profile.beta.toggle_hint"}}
              @checked={{this.betaExperience.isEnabled}}
              @onChange={{this.handleToggle}}
            />
            {{#if this.availableProducts.length}}
              <Text @variant="muted" @size="sm">
                {{t "profile.beta.per_product_description"}}
              </Text>
              {{#each this.availableProducts as |product|}}
                <Switch
                  data-test-id="beta-product-switch-{{product}}"
                  @label={{t (concat "profile.beta.products." product ".label")}}
                  @hint={{t (concat "profile.beta.products." product ".hint")}}
                  @checked={{this.betaExperience.isProductPreferenceEnabled product}}
                  @onChange={{fn this.handleProductToggle product}}
                  @isDisabled={{this.betaExperience.isEnabled}}
                />
              {{/each}}
            {{/if}}
            <Text @variant="muted" @size="sm">
              {{t "profile.beta.visual_description"}}
            </Text>
            <Switch
              data-test-profile-beta-chamfer-switch
              @label={{t "profile.beta.chamfer.label"}}
              @hint={{t "profile.beta.chamfer.hint"}}
              @checked={{this.betaExperience.isChamferEnabled}}
              @onChange={{this.handleChamferToggle}}
            />
          </VStack>
        </:body>
      </Section>
    {{/if}}
  </template>
}
